/**
 * Debounce function
 * @param {Function} func - The function to debounce
 * @param {boolean} immediate - Whether to execute the function immediately
 * @returns {Function} Debounced function
 */

import { endOfDay, parseISO, startOfDay, subDays } from 'date-fns'
import { t } from 'i18next'
import moment from 'moment'
import { matchPath } from 'react-router-dom'
import { COLUMN_MAPPINGS, E_SITE, EMAIL_REG_EXP, reportMap, typeFieldMap, WORK_ORDER_TYPES } from './constants'

export function debounce(func, immediate) {
  let timeout
  return function executedFunction() {
    const context = this
    const args = arguments

    const later = () => {
      timeout = null
      if (!immediate) func.apply(context, args)
    }

    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, 500)
    if (callNow) func.apply(context, args)
  }
}

/**
 * appendParams function
 * A function for appending query parameters to the current URL and updating the browser history
 * @param {Object} value - The object containing the parameters to be appended
 */
export function appendParams(value) {
  const params = parseParams(window.location.search)
  const data = { ...params, ...value }

  // Remove empty, invalid values, or stringified empty arrays
  Object.keys(data).forEach((key) => {
    if (data[key] === '' || !data[key]?.toString().length || data[key] === '[]') {
      delete data[key]
    }
  })

  // Exclude `start=0` if `search` is cleared
  if (!data.search && data.start === 0) {
    delete data.start
  }

  const queryString = new URLSearchParams(data).toString()
  const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname
  window.history.pushState({}, null, newUrl)
}

/**
 * parseParams function
 * A function for parsing query parameters from a given URL string or the current location
 * @param {string} params - The URL string containing the query parameters (default: '')
 * @returns {Object} An object containing the parsed query parameters
 */
export function parseParams(queryString = '') {
  const params = new URLSearchParams(queryString)
  const result = {}

  for (const [key, value] of params.entries()) {
    // Check if the value is supposed to be an array
    if (value.includes(',')) {
      result[key] = value.split(',') // Convert to array
    } else if (!isNaN(value)) {
      // Check if the value can be parsed as an integer
      result[key] = parseInt(value, 10) // Convert to integer
    } else if (value === 'true' || value === 'false') {
      // Check if the value is a boolean
      result[key] = value === 'true' // Convert to boolean
    } else {
      // Otherwise, keep it as a string
      result[key] = value // Keep as string
    }
  }

  return result
}

/**
 * setSortType function
 * @param {Array} data - The array of objects to set sort types for
 * @param {Object} payload - The payload containing the sort information
 * @param {string} payload.sSortBy - The internal name of the column to sort by
 * @param {number} payload.nOrder - The order (ascending or descending) of the sorting
 * @returns {Array} Updated array of objects with sort types set
 */
export function setSortType(data, payload) {
  return data.map((data) => {
    if (data.sInternalName === payload?.sSortBy) data.type = Number(payload?.nOrder)
    else data.type = 0
    return data
  })
}

/**
 * convertObjectToQueryString function
 * A function for converting an object into a query string and a filtered object
 * @param {Object} obj - The object to be converted into a query string
 * @returns {Object} An object containing the query string and the filtered object
 */
export function convertObjectToQueryString(obj) {
  const filteredParams = Object.fromEntries(
    Object.entries(obj)
      .filter(([, value]) => value !== undefined && value !== null && value !== '' && (!Array.isArray(value) || value.length > 0))
      .map(([key, value]) => [key, Array.isArray(value) ? JSON.stringify(value) : value])
  )

  return { queryString: new URLSearchParams(filteredParams).toString(), queryObject: filteredParams }
}

/**
 * formatDate function
 * A function for formatting a date string according to the provided format
 * @param {string} dateString - The date string to be formatted
 * @param {'YYYY-MM-DD' | 'YYYY/MM/DD' | 'YYYY-M-D' | 'YYYY/M/D' |
 *         'YY-MM-DD' | 'YY/MM/DD' | 'YY-M-D' | 'YY/M/D' |
 *         'MMMM D, YYYY' | 'MMMM D, YY' |
 *         'MM/DD/YYYY' | 'MM-DD-YYYY' | 'M/D/YYYY' | 'M-D-YYYY' |
 *         'MM/DD/YY' | 'MM-DD-YY' | 'M/D/YY' | 'M-D-YY' |
 *         'DD/MM/YYYY' | 'DD-MM-YYYY' | 'D/M/YYYY' | 'D-M-YYYY' |
 *         'DD/MM/YY' | 'DD-MM-YY' | 'D/M/YY' | 'D-M-YY' |
 *         'YYYY-MM-DD HH:mm:ss' | 'YYYY/MM/DD HH:mm:ss' |
 *         'YYYY-M-D HH:mm:ss' | 'YYYY/M/D HH:mm:ss' |
 *         'YY-MM-DD HH:mm:ss' | 'YY/MM/DD HH:mm:ss' |
 *         'YY-M-D HH:mm:ss' | 'YY/M/D HH:mm:ss' |
 *         'MM/DD/YYYY HH:mm:ss' | 'MM-DD-YYYY HH:mm:ss' |
 *         'M/D/YYYY HH:mm:ss' | 'M-D-YYYY HH:mm:ss' |
 *         'MM/DD/YY HH:mm:ss' | 'MM-DD-YY HH:mm:ss' |
 *         'M/D/YY HH:mm:ss' | 'M-D-YY HH:mm:ss' |
 *         'DD/MM/YYYY HH:mm:ss' | 'DD-MM-YYYY HH:mm:ss' |
 *         'D/M/YYYY HH:mm:ss' | 'D-M-YYYY HH:mm:ss' |
 *         'DD/MM/YY HH:mm:ss' | 'DD-MM-YY HH:mm:ss' |
 *         'D/M/YY HH:mm:ss' | 'D-M-YY HH:mm:ss'} format - The format string specifying the desired date format
 * @returns {string} The formatted date string
 */
export function formatDate(dateString, format = '') {
  if (!dateString) return null
  const date = new Date(dateString)
  const formatOptions = {
    YYYY: date.getFullYear(), // 2021
    YY: String(date.getFullYear()).slice(-2), // 21
    MMMM: new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date), // January
    MMM: new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date), // Jan
    EEE: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()], // Mon
    MM: String(date.getMonth() + 1).padStart(2, '0'), // 01
    M: date.getMonth() + 1, // 1
    DD: String(date.getDate()).padStart(2, '0'), // 01
    D: date.getDate(), // 1
    HH: String(date.getHours()).padStart(2, '0'), // 15
    hh: String(date.getHours() % 12 || 12).padStart(2, '0'), // 07 or 12
    mm: String(date.getMinutes()).padStart(2, '0'), // 01
    ss: String(date.getSeconds()).padStart(2, '0'), // 01
    A: date.getHours() >= 12 ? 'PM' : 'AM'
  }

  return format.replace(/YYYY|YY|MMMM|MMM|EEE|MM|M|DD|DO|D|HH|hh|mm|ss|A/g, (match) => formatOptions[match])
}
/**
 * emitEvent function
 * A function for emitting a custom event on the given element
 * @param {string} type - The type of the event
 * @param {Object} [detail={}] - The detail object of the event
 * @param {HTMLElement} [elem=document] - The element on which to emit the event
 * @returns {boolean} Whether the event was cancelled or not
 */
export function emitEvent(type, detail = {}, elem = document) {
  if (!type) return

  const event = new CustomEvent(type, {
    bubbles: true,
    cancelable: true,
    detail
  })

  return elem.dispatchEvent(event)
}
/**
 * navigationTo function
 * A function for navigating to the given link
 * @param {string} link - The link to navigate to
 */

export function navigationTo(link) {
  emitEvent('navigateTo', link)
}

/**
 * removeToken function
 * A function for removing the token from localStorage
 * @returns {void} - No return value
 */

export function removeToken() {
  localStorage.removeItem('token')
}

export function convertInDateFormat(date) {
  const parsedDate = new Date(date)

  return parsedDate
}
export function convertISOFormat(date) {
  if (!date) {
    return '' // Return an empty string if the date is undefined, null, or falsy
  }

  const parsedDate = new Date(date).toISOString()
  return parsedDate
}

export function convertCapitalize(text) {
  if (typeof text !== 'string' || !text.trim()) return text // Validate input

  const trimmedText = text.trim() // Remove leading/trailing spaces
  const result = trimmedText.charAt(0).toUpperCase() + trimmedText.slice(1).toLowerCase() // Capitalize first char, lowercase the rest

  return result
}

// Set item in storage.
export const setStorage = (type, key, value, options) => {
  try {
    if (typeof window === 'undefined') {
      console.warn(`Cannot set storage item "${key}" on the server.`)
      return
    }

    const stringValue = JSON.stringify(value)
    if (type === 1) {
      localStorage.setItem(key, stringValue)
    } else if (type === 2) {
      sessionStorage.setItem(key, stringValue)
    }
  } catch (error) {
    console.error(`Error setting storage item "${key}":`, error)
  }
}

// Get storage item
export const getStorage = (type, key) => {
  try {
    if (typeof window === 'undefined' && type !== 3) {
      console.warn(`Cannot get storage item "${key}" on the server.`)
      return null
    }

    let item = null

    if (type === 1) {
      item = localStorage.getItem(key)
    } else if (type === 2) {
      item = sessionStorage.getItem(key)
    }

    return item ? JSON.parse(item) : null
  } catch (error) {
    console.error(`Error getting storage item "${key}":`, error)
    return null
  }
}

// Clear storage item
export const clearStorage = (type, key) => {
  try {
    if (typeof window === 'undefined' && type !== 3) {
      console.warn(`Cannot clear storage item "${key}" on the server.`)
      return
    }

    if (type === 1) {
      localStorage.removeItem(key)
    } else if (type === 2) {
      sessionStorage.removeItem(key)
    }
  } catch (error) {
    console.error(`Error clearing storage item "${key}":`, error)
  }
}

// is Bottom reached
export function isBottomReached(id, callBack) {
  const controller = new AbortController()
  function handleScroll() {
    const ele = document.getElementById(id)
    if (ele) {
      if (ele.offsetTop <= window.scrollY + window.innerHeight) {
        callBack(ele.offsetTop <= window.scrollY + window.innerHeight)
        controller.abort()
      }
    }
  }
  window.addEventListener('scroll', handleScroll, { signal: controller.signal })
}

export const calculateAge = (birthDate) => {
  const birth = new Date(birthDate)
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const isBirthdayPassed =
    today.getMonth() > birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate())

  if (!isBirthdayPassed) {
    age--
  }

  return age
}

export const truncateText = (text, maxLength) => {
  if (text?.length > maxLength) {
    return text.substring(0, maxLength) + '...'
  }
  return text
}

// Helper function to render info section
export const renderInfoSection = (label, value, isImportant = false) => (
  <div className="mb-2">
    <p className="text-x text-gray-600">{label}</p>
    <p className={`font-medium truncate ${isImportant ? 'text-brand-600' : ''}`} title={value || 'N/A'}>
      {value || 'N/A'}
    </p>
  </div>
)

// calculate duration
export const calculateDuration = (startTime, endTime, isActive) => {
  if (!startTime) return 'N/A'

  let diffMs

  if (isActive) {
    const punchInDate = new Date(startTime)
    const now = new Date()
    diffMs = now - punchInDate
  } else {
    const punchInDate = new Date(startTime)
    const punchOutDate = new Date(endTime)
    diffMs = punchOutDate - punchInDate
  }

  const seconds = Math.floor((diffMs / 1000) % 60)
  const minutes = Math.floor((diffMs / (1000 * 60)) % 60)
  const hours = Math.floor((diffMs / (1000 * 60 * 60)) % 24)
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (days === 0) {
    return `${hours}h ${minutes}m ${seconds}s`
  }

  return `${days} ${days === 1 ? 'day' : 'days'} ${hours}h ${minutes}m`
}

// Common function to format array values with separator
export const getFormattedArrayValues = (array, propertyName = null, separator = ', ') => {
  if (!array || array.length === 0) return 'N/A'

  return array
    .map((item) => {
      // If propertyName is provided, extract that property from each item
      if (propertyName) {
        return item[propertyName]
      }
      // Otherwise just return the item itself
      return item
    })
    .join(separator)
}

export const getISODateRangeUTC = (startDate, endDate) => {
  const istOffset = 5.5 * 60 * 60 * 1000 // IST is UTC+5:30

  // Convert input dates to Date objects
  const start = new Date(startDate)
  const end = new Date(endDate)

  // Start of the given startDate in IST (00:00:00) converted to UTC
  const startOfDayUTC = new Date(start.setHours(0, 0, 0, 0) - istOffset).toISOString()

  // End of the given endDate in IST (23:59:59.999) converted to UTC
  const endOfDayUTC = new Date(end.setHours(23, 59, 59, 999) - istOffset).toISOString()

  return { startOfDayUTC, endOfDayUTC }
}

export const getNameInitials = (name) => {
  if (!name) return ''

  const nameParts = name.trim().split(/\s+/) // Split by spaces, handling multiple spaces
  if (nameParts.length === 1) return nameParts[0][0].toUpperCase() // Single word case

  const firstInitial = nameParts[0][0].toUpperCase() // First letter of the first name
  const lastInitial = nameParts[nameParts.length - 1][0].toUpperCase() // First letter of the last name

  return firstInitial + lastInitial
}

export const getReceiveOrderVisibility = (data) => {
  if (!data) return { hasPendingNewParts: false, hasPendingRepairedParts: false, hideTable: true }

  const parts = data.aParts || []
  const repaired = data.aRepairedParts || []

  const hasPendingNewParts = parts.some((p) => {
    const ordered = Number(p?.nOrderQuantity || 0)
    const received = Number(p?.nReceivedQuantity || 0)
    // pending ONLY when received < ordered
    return received < ordered
  })

  const hasPendingRepairedParts = repaired.some((p) => {
    const ordered = Number(p?.nOrderQuantity || 0)
    const received = Number(p?.nReceivedQuantity || 0)
    return received < ordered
  })

  const hideTable = !hasPendingNewParts && !hasPendingRepairedParts

  return { hasPendingNewParts, hasPendingRepairedParts, hideTable }
}

// keep old API working if other code uses it
export const shouldHideTable = (data) => {
  const { hideTable } = getReceiveOrderVisibility(data)
  return hideTable
}

export const formatToDecimalValue = (num) => {
  const val = num ?? 0
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(val)
}

export const formatToNumberValue = (num) => {
  const val = num ?? 0
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0
  }).format(val)
}

export function getReportById(id) {
  return reportMap.get(id) || null
}

export const transformPurchaseOrderData = (data) => {
  return data.map((item) => ({
    'Order No': item.nPurchaseOrderId || '',
    Vendor: item.oVendor?.sName || '',
    Status: item.eStatus || '',
    'PO Date': item.dPODate ? moment(item.dPODate).format('YYYY-MM-DD') : '',
    'Expected Date': item.dExpectedDate ? moment(item.dExpectedDate).format('YYYY-MM-DD') : '',
    Subtotal: item.nSubTotal ?? '',
    'Total Quantity': item.nTotalOrderQuantity ?? '',
    Item: item.aParts?.[0]?.iInventoryId?.sCategoryType || '',
    'item Name': item.aParts?.[0]?.iInventoryId?.sName || ''
  }))
}
export const transformInventory = (data) => {
  return data.map((item) => ({
    Item: item.sCategoryType || '',
    'Item Name': item.sName || '',
    Category: item.sCategory || '',
    'Stock Price': item.nCurrentRate ?? '',
    Unit: item.sSymbol || '',
    Stock: item.nQuantity ?? '',
    'Stock Alert': item.nAlertQuantity ?? '',
    'Tax Rate': item.nTaxRate ?? ''
  }))
}
// ? Transform vehicle data for Excel download
export const transformVehicleData = (data) => {
  return data.map((item) => ({
    RegistrationNo: item.sRegistrationNo,
    ShortName: item.sShortName,
    Model: item.sModel,
    OwnerName: item.sOwnerName,
    Type: item.eType,
    Company: item.iCompanyId?.sShortName || '',
    Status: item.eStatus,
    RegistrationDate: item.dRegistrationDate ? moment(item.dRegistrationDate).format('YYYY-MM-DD') : '',
    FitnessValidity: item.dFitnessValidity ? moment(item.dFitnessValidity).format('YYYY-MM-DD') : '',
    InsuranceValidity: item.dInsuranceValidity ? moment(item.dInsuranceValidity).format('YYYY-MM-DD') : '',
    PUCValidity: item.dPUCValidity ? moment(item.dPUCValidity).format('YYYY-MM-DD') : '',
    PermitValidity: item.dPermitValidity ? moment(item.dPermitValidity).format('YYYY-MM-DD') : ''
    // Add more fields as needed
  }))
}
// ? TransformLeave data for Excel download
export const transformLeaveData = (data) =>
  data.map((item) => ({
    Name: item.oEmployee?.sName || '',
    Designation: item.oEmployee?.sDesignation || '',
    Number: item.oEmployee?.sMobNum || '',
    'Start Date': item.dStartDate ? new Date(item.dStartDate).toLocaleDateString() : '',
    'End Date': item.dEndDate ? new Date(item.dEndDate).toLocaleDateString() : ''
  }))

// ? Transform Manpower data for Excel download
export const transformManpowerData = (data) =>
  data.map((item) => ({
    Name: item?.sName || '',
    'Father Name': item?.sFatherName || '',
    Mobile: item?.sMobNum || '',
    Designation: item?.sDesignation || '',
    Address: item?.sAddress || '',
    IdCard: item?.nIdCard || '',
    'Aadhaar No': item?.oAadhaar?.sNo || '',
    'Birth Date': item?.dBirthDate ? new Date(item?.dBirthDate).toLocaleDateString() : '',
    'Join Date': item?.dJoinDate ? new Date(item?.dJoinDate).toLocaleDateString() : '',
    'Company Name': item?.iCompanyId?.sShortName || ''
  }))
// ? Movement data for Excel download
export const getMovementLabel = (actionType) => {
  if (actionType === 'RECEIVE') {
    return { label: t('in'), className: 'text-success-700 bg-success-100' }
  } else if (actionType === 'ISSUE') {
    return { label: t('out'), className: 'text-red-700 bg-red-100' }
  } else if (actionType === 'PURCHASE-RETURN') {
    return { label: t('return'), className: 'text-warning-700 bg-warning-100' }
  }
  return { label: actionType, className: 'text-gray-700 bg-gray-100' }
}
// ? Transform Stock Inevntory data for Excel download
export const transformStockInventory = (data) => {
  return data.map((item) => ({
    Item: item.oInventory?.sName || '',
    Reference: item.oVehicle?.sRegistrationNo || '',
    'Item Name': item?.oInventory?.sName || '',
    Category: item?.oInventory?.sCategory || '',
    'Stock Price': item.nNewRate ?? '',
    Stock: item.nQuantity ?? '',
    movement: getMovementLabel(item.eActionType)?.label
  }))
}

// ? Transform Issued Stock data (Stock Issue page) for Excel download
export const transformIssuedStock = (data) => {
  return data.map((item) => ({
    'Item Name': item?.oInventory?.sName || '',
    Category: item?.oInventory?.sCategory || '',
    Company: item?.oCompany?.sShortName || '',
    'Stock Issued on': item?.dActionDate
      ? moment(item?.dActionDate).format('DD MMM YYYY, hh:mm A')
      : item?.dCreatedAt
        ? moment(item?.dCreatedAt).format('DD MMM YYYY, hh:mm A')
        : '',
    'Equipment No.': item?.oVehicle?.sRegistrationNo || '',
    Quantity: item?.nQuantity ?? '',
    'Item Price': item?.nRate ?? '',
    'Total Price': item?.nTotalPrice ?? '',
    Movement: getMovementLabel(item?.eActionType)?.label || '',
    'Issued/Created by': item?.oCreatedBy ? `${item?.oCreatedBy?.sFirstName || ''} ${item?.oCreatedBy?.sLastName || ''}`.trim() : ''
  }))
}

// ? Transform Received Stock data (Stock History page) for Excel download
export const transformReceivedStock = (data) => {
  return data.map((item) => ({
    'Item Name': item?.oInventory?.sName || '',
    Category: item?.oInventory?.sCategory || '',
    Company: item?.oCompany?.sShortName || '',
    'PO No.': item?.oPurchaseOrder?.nPurchaseOrderId || '',
    'Vendor Invoice No.': item?.sInvoiceNo || item?.oPurchaseOrder?.sInvoiceNo || '',
    'PO Created On': item?.oPurchaseOrder?.dPODate ? moment(item?.oPurchaseOrder?.dPODate).format('DD MMM YYYY') : '',
    'Stock Received On': item?.dActionDate ? moment(item?.dActionDate).format('DD MMM YYYY, hh:mm A') : '',
    Quantity: item?.nQuantity ?? '',
    'Item Price': item?.nRate ?? '',
    'Total Price': item?.nTotalPrice ?? '',
    Movement: getMovementLabel(item?.eActionType)?.label || '',
    'Created By': item?.oCreatedBy ? `${item?.oCreatedBy?.sFirstName || ''} ${item?.oCreatedBy?.sLastName || ''}`.trim() : ''
  }))
}

export const transformReportColumns = (data) => {
  return data?.map((item) => ({
    Date: item.dDate || '',
    'Company Name': item.sCompanyName || '',
    'Tipper No.': item?.sRegistrationNo || '',
    'Punch-In': item?.dPunchIn || '',
    'Punch-Out': item.dPunchOut ?? '',
    'Total Time': item.sTotalTime ?? '',
    'Driver Name': item.sDriverName ?? '',
    'Mechanic Name': item.sMechanicName ?? '',
    'Final Note': item.sFinalNote ?? ''
  }))
}

export function getReportQueryParams(searchParams) {
  const companyId = searchParams.get('company') === 'all' ? null : searchParams.get('company')
  const categoryId = searchParams.get('category') === 'all' ? null : searchParams.get('category')
  const tipperNo = searchParams.get('tipperNo')
  const lubeId = searchParams.get('lube')
  const vendorId = searchParams.get('vendor')
  const dateRangeParam = searchParams.get('dateRange')
  const isYesterday = dateRangeParam === 'yesterday'
  const isCustomRange = dateRangeParam === 'custom'

  let dateFrom, dateTo

  if (isCustomRange) {
    const start = parseISO(searchParams.get('startDate'))
    const end = parseISO(searchParams.get('endDate'))
    dateFrom = startOfDay(start)
    dateTo = endOfDay(end)
  } else if (isYesterday) {
    const yesterday = subDays(new Date(), 1)
    dateFrom = startOfDay(yesterday)
    dateTo = endOfDay(yesterday)
  } else if (dateRangeParam) {
    const dateRange = Number(dateRangeParam)
    const numDays = dateRange - 1

    dateTo = endOfDay(new Date())
    dateFrom = startOfDay(subDays(dateTo, numDays))
  }

  return {
    iCompanyId: companyId,
    iVehicleId: tipperNo,
    sCategoryType: categoryId,
    iLubeId: lubeId,
    iVendorId: vendorId,
    dateFrom: dateFrom?.toISOString(),
    dateTo: dateTo?.toISOString()
  }
}

export function transformReportDataColumns(data = [], columns = []) {
  return data.map((item, index) => {
    const row = {}

    columns.forEach(({ name, label }) => {
      if (name === 'sno') {
        row[label] = index + 1
      } else {
        const value = item[name]

        if (['dDate'].includes(name)) {
          row[label] = value ? formatDate(value, 'DD/MM/YYYY') : ''
        } else if (['dPunchIn', 'dPunchOut'].includes(name)) {
          row[label] = value ? formatDate(value, 'HH:mm A') : ''
        } else if (['eWorkOrderType', 'eSite'].includes(name)) {
          row[label] = WORK_ORDER_TYPES.find((item) => item?.value === value)?.label || E_SITE.find((item) => item?.value === value)?.label || ''
        } else {
          row[label] = value !== undefined && value !== null ? value : ''
        }
      }
    })

    return row
  })
}

export function getFilterInfoRows(filterValues = {}, reportData = {}) {
  if (!reportData.filters) return []

  return Object.entries(filterValues).flatMap(([key, val]) => {
    if (!val) return []

    const filterMeta = reportData.filters.find((f) => f.name === key)
    const label = `${t(filterMeta?.name === 'vendor' ? 'vendors' : filterMeta?.name === 'tipperNo' ? 'equipment' : filterMeta?.name)}:`

    if (key === 'dateRange') {
      if (val.value === 'custom') {
        return [[label, `${formatDate(val.startDate, 'DD/MM/YYYY')} - ${formatDate(val.endDate, 'DD/MM/YYYY')}`]]
      } else if (val.value === 'yesterday') {
        return [[label, `Yesterday (${formatDate(val.startDate, 'DD/MM/YYYY')} - ${formatDate(val.endDate, 'DD/MM/YYYY')})`]]
      } else {
        return [[label, `Last ${val.value} days (${formatDate(val.startDate, 'DD/MM/YYYY')} - ${formatDate(val.endDate, 'DD/MM/YYYY')})`]]
      }
    } else if (Array.isArray(val)) {
      return [[label, val.map((v) => v.label).join(', ')]]
    } else if (val?.label || val?.value) {
      return [[label, val.label]]
    }

    return []
  })
}

export const getFormattedDashboardStats = (data) => {
  if (!data) return []

  return [
    {
      label: 'TransportationandLoading',
      subStats: [
        { label: 'tonnage', value: data?.oTotalWork?.nTonnage, formatTo: 'decimal' },
        { label: 'amount', value: data?.oTotalWork?.nAmount, formatTo: 'decimal' }
      ]
    },
    {
      label: 'Stockyard',
      subStats: [
        { label: 'tonnage', value: data?.oStockData?.nTonnage, formatTo: 'decimal' },
        { label: 'avgTonTrip', value: data?.oStockData?.nTonnageAvg, formatTo: 'decimal' },
        { label: 'amount', value: data?.oStockData?.nAmount, formatTo: 'decimal' }
      ]
    },
    {
      label: 'Reject',
      subStats: [
        {
          label: 'tonnage',
          value: data?.oRejectData?.nTonnage,
          formatTo: 'decimal'
        },
        {
          label: 'avgTonTrip',
          value: data?.oRejectData?.nTonnageAvg,
          formatTo: 'decimal'
        },
        {
          label: 'amount',
          value: data?.oRejectData?.nAmount,
          formatTo: 'decimal'
        }
      ]
    },
    {
      label: 'Siding',
      subStats: [
        {
          label: 'tonnage',
          value: data?.oSidingData?.nTonnage,
          formatTo: 'decimal'
        },
        {
          label: 'avgTonTrip',
          value: data?.oSidingData?.nTonnageAvg,
          formatTo: 'decimal'
        },
        {
          label: 'amount',
          value: data?.oSidingData?.nAmount,
          formatTo: 'decimal'
        }
      ]
    },
    {
      label: 'TotalSRS',
      subStats: [
        {
          label: 'tonnage',
          value: data?.oTotalTransportation?.nTonnage,
          formatTo: 'decimal'
        },
        {
          label: 'amount',
          value: data?.oTotalTransportation?.nAmount,
          formatTo: 'decimal'
        }
      ]
    },
    {
      label: 'tippersOnRoad',
      value: data?.oTransportData?.nTipperOnRoad
    },
    {
      label: 'totalTrips',
      value: data?.oTransportData?.nTotalTrip
    },
    {
      label: 'avgTripTipper',
      value: data?.oTransportData?.nAvgTrip,
      formatTo: 'decimal'
    },
    {
      label: 'avgTonTrip',
      value: data?.oTransportData?.nTonnageAvg,
      formatTo: 'decimal'
    },
    {
      label: 'payloadersOnRoad',
      value: data?.oLoadingData?.nPayLoaderOnRoad
    },
    {
      label: 'totalHoursWorked',
      value: data?.oLoadingData?.nTotalHour
    },
    {
      label: 'totalTonnage',
      value: data?.oLoadingData?.nTotalTonnage,
      formatTo: 'decimal'
    },
    {
      label: 'avgTonnHour',
      value: data?.oLoadingData?.nAvgHour,
      formatTo: 'decimal'
    },
    {
      label: 'totalLoading',
      subStats: [
        {
          label: 'tonnage',
          value: data?.oTotalLoading?.nTonnage,
          formatTo: 'decimal'
        },
        {
          label: 'amount',
          value: data?.oTotalLoading?.nAmount,
          formatTo: 'decimal'
        }
      ]
    }
  ]
}

export const getFormattedKeyMetricsStats = (data) => {
  if (!data) return []

  const bgColors = [
    'bg-gradient-to-r from-purple-200 to-purple-100 border-purple-200',
    'bg-gradient-to-r from-brand-200 to-brand-100 border-brand-200',
    'bg-gradient-to-r from-success-200 to-success-100 border-success-200',
    'bg-gradient-to-r from-purple-200 to-purple-100 border-purple-200',
    'bg-gradient-to-r from-brand-200 to-brand-100 border-brand-200',
    'bg-gradient-to-r from-success-200 to-success-100 border-success-200'
  ]

  const statsArray = [
    {
      label: 'equipmentDeployAtSite',
      subStats: [
        { label: 'tippers', value: data?.oEquipmentDeployment?.nTipper },
        { label: 'payloaders', value: data?.oEquipmentDeployment?.nPayLoader }
      ]
    },
    {
      label: 'expenseAtWorkshop',
      subStats: [
        { label: 'totalAmount', value: data?.oExpenseAtWorkshop?.nTotalAmount, formatTo: 'decimal' },
        { label: 'tippers', value: data?.oExpenseAtWorkshop?.nTipper, isSpace: true },
        { label: 'payloaders', value: data?.oExpenseAtWorkshop?.nPayLoader, isSpace: true },
        { label: 'others', value: data?.oExpenseAtWorkshop?.nOther, isSpace: true }
      ]
    },
    {
      label: 'manpowerAtCamp',
      subStats: [
        { label: 'drivers', value: data?.oManpowerAtCamp?.nDriver },
        { label: 'operators', value: data?.oManpowerAtCamp?.nOperator },
        { label: 'supervisors', value: data?.oManpowerAtCamp?.nSupervisor },
        { label: 'others', value: data?.oManpowerAtCamp?.nOther }
      ]
    },
    {
      label: 'equipmentInWorkshop',
      subStats: [
        { label: 'tippers', value: data?.oEquipmentInWorkshop?.nTipper },
        { label: 'payloaders', value: data?.oEquipmentInWorkshop?.nPayLoader }
      ]
    },
    {
      label: 'hsdConsumption',
      subStats: [
        { label: 'totalHSD', value: data?.oHsdConsumption?.nTotalAmount, formatTo: 'decimal' },
        { label: 'forTippers', value: data?.oHsdConsumption?.nTipper, isSpace: true },
        { label: 'forPayloaders', value: data?.oHsdConsumption?.nOperator, isSpace: true },
        { label: 'forOthers', value: data?.oHsdConsumption?.nOther, isSpace: true },
        { label: 'stockAtHand', value: data?.oHsdConsumption?.nStockInHand }
      ]
    },
    {
      label: 'manpowerOnLeave',
      subStats: [
        { label: 'drivers', value: data?.oEmployeeLeave?.nDriver },
        { label: 'operators', value: data?.oEmployeeLeave?.nOperator },
        { label: 'supervisors', value: data?.oEmployeeLeave?.nSupervisor },
        { label: 'others', value: data?.oEmployeeLeave?.nOther }
      ]
    }
  ]

  return statsArray.map((stat, index) => ({ ...stat, bg: bgColors[index] }))
}

export const getFormattedOperationsStats = (data) => {
  if (!data) return []

  return [
    {
      label: 'equipmentDeployAtSite',
      subStats: [
        { label: 'tippers', value: data?.oEquipmentDeployment?.nTipper },
        {
          label: 'payloaders',
          value: data?.oEquipmentDeployment?.nPayLoader
        }
      ]
    },
    // {
    //   label: 'Expense at Workshop',
    //   subStats: [{ label: '', value: data?.oExpenseAtWorkshop }]
    // },
    // {
    //   label: 'Manpower at Camp',
    //   subStats: [{ label: 'tonnage', value: data?.oManpowerAtCamp }]
    // },
    {
      label: 'equipmentInWorkshop',
      subStats: [
        { label: 'tippers', value: data?.oEquipmentInWorkshop?.nTipper },
        { label: 'payloaders', value: data?.oEquipmentInWorkshop?.nPayLoader }
      ]
    }
    // {
    //   label: 'HSD Consumption',
    //   subStats: [{ label: 'tonnage', value: data?.oTotalTransportation?.nTonnage }]
    // },
    // {
    //   label: 'manpowerOnLeave',
    //   subStats: [
    //     { label: 'drivers', value: data?.oEmployeeLeave?.nDriver },
    //     { label: 'operators', value: data?.oEmployeeLeave?.nOperator },
    //     { label: 'supervisors', value: data?.oEmployeeLeave?.nSupervisor },
    //     { label: 'others', value: data?.oEmployeeLeave?.nOther }
    //   ]
    // }
  ]
}

export const isDurationExceeding48Hours = (duration) => {
  if (!duration) return false

  const daysMatch = duration.match(/(\d+)\s*days?/)
  const hoursMatch = duration.match(/(\d+)\s*h/)
  const minutesMatch = duration.match(/(\d+)\s*m/)

  const days = daysMatch ? parseInt(daysMatch[1], 10) : 0
  const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0
  const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0

  const totalHours = days * 24 + hours + minutes / 60

  return totalHours > 48
}

export const findBreadcrumbTrail = (pathname, menuItems, params = {}) => {
  for (const item of menuItems) {
    const getLabel = () => (typeof item.breadcrumb === 'function' ? item.breadcrumb({ params }) : item.breadcrumb || item.title)

    if (item.link && matchPath({ path: item.link }, pathname)) {
      if (pathname === '/manage-trip') {
        return [{ label: 'equipmentDeploymentDaily', to: item.link }]
      }
      return [{ label: getLabel(), to: item.link }]
    }

    if (item.children) {
      const childTrail = findBreadcrumbTrail(pathname, item.children, params)
      if (childTrail.length) {
        return [{ label: item.title, to: item.link || '#' }, ...childTrail]
      }
    }
  }

  return []
}

// Helper to get status details for a given type and data row
export const getStatusDetails = (type, data) => {
  const currentDate = new Date()
  currentDate.setHours(0, 0, 0, 0)

  let validityDate

  if (type === 'PUC') {
    validityDate = new Date(data?.dPUCValidity)
  } else if (type === 'DL') {
    validityDate = new Date(data?.dExpiryDate)
  } else if (type === 'FITNESS') {
    validityDate = new Date(data?.dFitnessValidity)
  } else if (type === 'INSURANCE') {
    validityDate = new Date(data?.dInsuranceValidity)
  } else if (type === 'PERMIT') {
    validityDate = new Date(data?.dPermitValidity)
  } else if (type === 'IME') {
    validityDate = new Date(data?.dIMEExpiryDate)
  } else if (type === 'VTC') {
    validityDate = new Date(data?.sVTExpiryDate)
  }

  if (!validityDate || isNaN(validityDate.getTime())) {
    return { text: 'N/A', classes: 'text-gray-700 bg-gray-50' }
  }

  validityDate.setHours(0, 0, 0, 0)
  const daysDifference = Math.floor((validityDate - currentDate) / (1000 * 60 * 60 * 24))

  if (daysDifference < 0) {
    return {
      text: `Expired on ${formatDate(validityDate, 'MMM DD, YYYY')}`,
      classes: 'text-red-700 bg-red-100'
    }
  } else if (daysDifference <= 180) {
    return {
      text: `Expiring soon ${formatDate(validityDate, 'MMM DD, YYYY')}`,
      classes: 'text-orange-700 bg-orange-50'
    }
  } else {
    return {
      text: `Valid till ${formatDate(validityDate, 'MMM DD, YYYY')}`,
      classes: 'text-success-700 bg-success-100'
    }
  }
}

export const transformPendingDrivers = (data, type) => {
  if (!Array.isArray(data) || data.length === 0) return []

  // Pick the correct mapping based on type, fallback to all fields if not found
  const map = typeFieldMap[type?.toLowerCase()] || {}

  return data.map((row) => {
    const newRow = {}
    Object.entries(map).forEach(([apiKey, excelKey]) => {
      if (apiKey === 'iCompanyId') {
        newRow[excelKey] = row.iCompanyId?.sShortName || ''
      } else {
        newRow[excelKey] = row[apiKey] ?? ''
      }
    })
    // Add Expired Date column using getStatusDetails, except for vtc and ime
    if (type?.toLowerCase() !== 'vtc' && type?.toLowerCase() !== 'ime') {
      const expiredInfo = getStatusDetails(type?.toUpperCase(), row)
      let expiredDate = ''
      if (expiredInfo && expiredInfo.text) {
        const match = expiredInfo.text.match(/\b(?:on|soon|till)\s(.+)$/i)
        if (match && match[1]) {
          expiredDate = match[1]
        }
      }
      newRow['Expired Date'] = expiredDate
    }
    return newRow
  })
}

export const getFormattedWorkshopStats = (data) => {
  if (!data) return []

  return [
    {
      label: 'equipmentInWorkshop',
      subStats: [
        { label: 'noOfTippers', value: data?.nTipperInWorkshop },
        { label: 'noOfPayloaders', value: data?.nPayLoaderInWorkshop },
        { label: 'noOfOthers', value: data?.nOtherInWorkshop }
      ]
    },
    {
      label: 'equipmentInWorkshop48Hours',
      subStats: [
        { label: 'noOfTippers', value: data?.nTipperLongStayingVehicles },
        { label: 'noOfPayloaders', value: data?.nPayLoaderLongStayingVehicles },
        { label: 'noOfOthers', value: data?.nOtherLongStayingVehicles }
      ]
    }
    // {
    //   label: 'manpower',
    //   subStats: [
    //     { label: 'noOfMechanics', value: data?.oTotalWork?.nTonnage },
    //     { label: 'noOfHelpers', value: data?.oTotalWork?.nTonnage },
    //     { label: 'noOfOthers', value: data?.oTotalWork?.nOtherInWorkshop }
    //   ]
    // }
  ]
}

// Format value based on column format type
export const formatColumnValue = (val, f) => {
  if (f === 'decimal') {
    return formatToDecimalValue(Number(val ?? 0))
  }

  if (val === null || val === undefined) {
    return 0
  }

  if (isNaN(val)) {
    return val
  }

  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0
  }).format(Number(val))
}

export function getDateRangeFromParams(searchParams) {
  const dateRangeParam = searchParams.get('dateRange')
  const isCustomRange = dateRangeParam === 'custom'
  const isYesterday = dateRangeParam === 'yesterday'
  const isToday = dateRangeParam === 'today'

  let dateFrom, dateTo

  if (isCustomRange) {
    const start = parseISO(searchParams.get('startDate'))
    const end = parseISO(searchParams.get('endDate'))

    dateFrom = startOfDay(start)
    dateTo = endOfDay(end)
  } else if (isToday) {
    const today = new Date()
    dateFrom = startOfDay(today)
    dateTo = endOfDay(today)
  } else if (isYesterday || !dateRangeParam) {
    const yesterday = subDays(new Date(), 1)
    dateFrom = startOfDay(yesterday)
    dateTo = endOfDay(yesterday)
  } else {
    const dateRange = Number(dateRangeParam)
    const numDays = dateRange - 1

    dateTo = endOfDay(new Date())
    dateFrom = startOfDay(subDays(dateTo, numDays))
  }

  return {
    dateFrom,
    dateTo,
    isoDateFrom: dateFrom.toISOString(),
    isoDateTo: dateTo.toISOString()
  }
}

// // ? Download feature mapping values
// // Column mappings for each report type
// export const COLUMN_MAPPINGS = {
//   'production-tippers': {
//     'Short Name': 'oCompany.sShortName',
//     'Registration No': 'oVehicle.sRegistrationNo',
//     'Shift ': 'oShift.sName',
//     'Stockyard Trips': 'oStock.nTrip',
//     'Stockyard Tonnage': 'oStock.nTonnage',
//     'Stockyard Tonnage Avg': 'oStock.nAvgTonnage',
//     'Reject Trips': 'oReject.nTrip',
//     'Reject Tonnage': 'oReject.nTonnage',
//     'Reject Tonnage Avg': 'oReject.nAvgTonnage',
//     'Siding Trips': 'oSiding.nTrip',
//     'Siding Tonnage': 'oSiding.nTonnage',
//     'Siding Tonnage Avg': 'oSiding.nAvgTonnage',
//     'Total Srs Trips': 'oTotal.nTrip',
//     'Total Srs Tonnage': 'oTotal.nTonnage',
//     'Total Srs Tonnage Avg': 'oTotal.nAvgTonnage',
//     'Revenue ': 'nAmount'
//   },
//   'production-payloaders': {
//     'Company Name': 'sCompany',
//     'Payloader Name': 'sVehicle',
//     'Shift ': 'sShift',
//     'No of Hour Engaged': 'nHour',
//     'Total Tonnage': 'nTonnage',
//     'Avg. Tonnage Per Trip': 'nTonnageHour'
//   },
//   'tipper-no': {
//     'Date ': 'dDate',
//     'Company Name': 'oCompany.sName',
//     'From Name of Face': 'oSiteFrom.sName',
//     'Shift ': 'oShift.sName',
//     'To Destination': 'oSiteTo.sName',
//     'Lead ': (item) => item?.sFaceLeadKey + item?.sSurfaceLeadKey,
//     'Tonnage Total': 'nWeight',
//     'No of Trips': 'nTrip',
//     'Avg. Tonnage Per Trip': 'avgTonnagePerTrip',
//     'Rate(Rs)': 'nRate',
//     'Amount(Rs)': 'nAmount'
//   },
//   'payloader-no': {
//     'Date ': 'dDate',
//     'Payloader No': 'oVehicle.sRegistrationNo',
//     'Vehicle Short Name': 'oVehicle.sShortName',
//     'Site Name': 'oSite.sName',
//     'Company Name': 'oCompany.sName',
//     'Operator Name': 'oEmployee.sName',
//     'Tonnage Total': 'nWeight',
//     'Shift Name': 'oShift.sName',
//     'Avg. Tonnage/Hour': 'avgTonnagePerTrip',
//     'Rate(Rs)': 'nRate',
//     'Amount(Rs)': 'nAmount'
//   }
// }

// // ? File name mappings
// export const FILE_NAMES = {
//   'production-tippers': 'Production_Tippers',
//   'production-payloaders': 'Production_Payloaders',
//   'tipper-no': 'Production_Tipper_No',
//   'payloader-no': 'Production_Payloader_No'
// }

// ? Helper function to get nested object value
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj)
}

// ? Generic function to build filter descriptions
export const buildFilterDescriptions = (searchParams, selectedFilters) => {
  const filterDescriptions = []
  const { isoDateFrom, isoDateTo } = getDateRangeFromParams(searchParams)

  // Add date range information
  if (isoDateFrom && isoDateTo) {
    const formattedFromDate = formatDate(new Date(isoDateFrom), 'DD/MM/YYYY')
    const formattedToDate = formatDate(new Date(isoDateTo), 'DD/MM/YYYY')
    if (formattedFromDate === formattedToDate) {
      filterDescriptions.push(['Date:', formattedFromDate])
    } else {
      filterDescriptions.push(['Date Range:', `${formattedFromDate} - ${formattedToDate}`])
    }
  }

  // ? Add other filters
  const filterMapping = {
    companies: 'Company:',
    shifts: 'Shift:',
    competitors: 'Competitor:',
    vehicles: 'Tipper:',
    payloaders: 'Payloader:'
  }
  // ? Map selected filter values to descriptions
  Object.entries(filterMapping).forEach(([key, label]) => {
    if (selectedFilters[key]?.length > 0) {
      const names = selectedFilters[key].map((item) => item.label).join(', ')
      filterDescriptions.push([label, names])
    }
  })

  // ? Add empty row for separation
  if (filterDescriptions.length > 0) {
    filterDescriptions.push([])
  }

  return filterDescriptions
}

// ? Generic transform data function
export const createTransformData = (reportType, selectedFilters, searchParams) => {
  return (rows) => {
    const filterDescriptions = buildFilterDescriptions(searchParams, selectedFilters)
    const columnMapping = COLUMN_MAPPINGS[reportType]
    const sourceRows = rows || []

    // Pages whose columns are determined dynamically (e.g. when categories
    // come from the API) skip the static mapping and emit rows that are
    // already keyed by their display column names. Use the union of keys
    // across rows so optional columns aren't dropped if the first row is
    // missing them.
    let dataRows
    if (columnMapping) {
      dataRows = sourceRows.map((item) => {
        const row = {}
        Object.entries(columnMapping).forEach(([columnName, path]) => {
          if (typeof path === 'function') {
            row[columnName] = path(item)
          } else {
            row[columnName] = getNestedValue(item, path)
          }
        })
        return row
      })
    } else {
      dataRows = sourceRows
    }

    // If no data, just return filter descriptions
    if (dataRows.length === 0) {
      return filterDescriptions
    }

    let csvHeader
    if (columnMapping) {
      csvHeader = Object.keys(columnMapping)
    } else {
      const headerSet = new Set()
      for (const row of dataRows) {
        for (const key of Object.keys(row || {})) headerSet.add(key)
      }
      csvHeader = Array.from(headerSet)
    }
    const csvRows = dataRows.map((row) => csvHeader.map((key) => row?.[key]))

    return [...filterDescriptions, csvHeader, ...csvRows]
  }
}

// ? Get report type from pathname
export const getReportTypeFromPath = (pathname) => {
  if (pathname.includes('/stores-consumed-tippers/tipper-no')) return 'stores-consumed-tipper-no'
  if (pathname.includes('/stores-consumed-payloaders/payloader-no')) return 'stores-consumed-payloader-no'
  if (pathname.includes('/stores-consumed-all-others/other-no')) return 'stores-consumed-other-no'
  if (pathname.includes('/stores-consumed-tippers')) return 'stores-consumed-tippers'
  if (pathname.includes('/stores-consumed-payloaders')) return 'stores-consumed-payloaders'
  if (pathname.includes('/stores-consumed-other')) return 'stores-consumed-other'
  if (pathname.includes('/production-tippers')) return 'production-tippers'
  if (pathname.includes('/production-payloaders')) return 'production-payloaders'
  if (pathname.includes('/tipper-no')) return 'tipper-no'
  if (pathname.includes('/payloader-no')) return 'payloader-no'
  return null
}

export const getStatusColor = (status) => {
  switch (status) {
    case 'On Road':
      return 'bg-success-50 text-success-700 border-success-200'
    case 'Idle':
      return 'bg-warning-50 text-warning-700 border-warning-200'
    case 'In Workshop':
      return 'bg-red-50 text-red-700 border-red-200'
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200'
  }
}

// Email validation function for tags mode
export const isValidEmail = (email) => {
  const emailRegex = EMAIL_REG_EXP
  return emailRegex.test(email)
}

export const generateCsvFilename = (title) => {
  const updatedTitle = title.replace(/[^\w\s-]/g, '').replace(/\s+/g, '-')

  return updatedTitle
}
